'use client';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOnSyncEvent, useSyncBusContext } from '@/hooks/use-sync-bus';
import {
  AUTO_RECOVERY_COOLDOWN_DEFAULT_MS,
  AUTO_RECOVERY_MAX_ATTEMPTS,
  clampAutoRecoveryCooldownMs,
} from '@/lib/types/sync-events';
import { captureClientPosthogEvent } from '@/lib/utils/capture-client-posthog';
import { isChunkLoadError } from '@/lib/utils/is-chunk-load-error';

const RECOVERY_STORAGE_KEY = 'red-client-recovery';
const AUTO_RECOVERY_EFFECTIVE_KEY = 'red-auto-recovery-effective';
const AUTO_RECOVERY_COOLDOWN_KEY = 'red-auto-recovery-cooldown-ms';
const POSTHOG_EVENT_CHUNK_RECOVERED = 'client_chunk_error_auto_recovered';
const POSTHOG_EVENT_CHUNK_GAVE_UP = 'client_chunk_error_recovery_skipped';
const POSTHOG_EVENT_CHUNK_BUDGET_EXHAUSTED = 'client_chunk_error_attempt_budget_exhausted';
const POSTHOG_EVENT_CHUNK_NOT_OPTED_IN = 'client_chunk_error_auto_recovery_disabled';
const POSTHOG_EVENT_REMOTE_REFRESH = 'client_remote_refresh_received';
const POSTHOG_EVENT_EFFECTIVE_CHANGED = 'client_auto_recovery_effective_changed';
const POSTHOG_EVENT_COOLDOWN_CHANGED = 'client_auto_recovery_cooldown_changed';

interface RecoveryRecord {
  lastReloadAt: number;
  reason: string;
  attempts?: number;
}

function readSessionStorageItem(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionStorageItem(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch { /* best-effort */ }
}

function removeSessionStorageItem(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(key);
  } catch { /* best-effort */ }
}

function readRecoveryRecord(): RecoveryRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = readSessionStorageItem(RECOVERY_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as RecoveryRecord;
  } catch {
    return null;
  }
}

function writeRecoveryRecord(reason: string, attempts: number): void {
  if (typeof window === 'undefined') return;
  writeSessionStorageItem(
    RECOVERY_STORAGE_KEY,
    JSON.stringify({
      lastReloadAt: Date.now(),
      reason,
      attempts,
    } satisfies RecoveryRecord),
  );
}

function writeRemoteRefreshRecord(reason: string): void {
  if (typeof window === 'undefined') return;
  writeSessionStorageItem(
    RECOVERY_STORAGE_KEY,
    JSON.stringify({
      lastReloadAt: Date.now(),
      reason,
      attempts: 0,
    } satisfies RecoveryRecord),
  );
}

function isAutoRecoveryEffective(): boolean {
  if (typeof window === 'undefined') return false;
  return readSessionStorageItem(AUTO_RECOVERY_EFFECTIVE_KEY) === '1';
}

function persistAutoRecoveryEffective(effective: boolean): void {
  if (typeof window === 'undefined') return;
  if (effective) {
    writeSessionStorageItem(AUTO_RECOVERY_EFFECTIVE_KEY, '1');
  } else {
    removeSessionStorageItem(AUTO_RECOVERY_EFFECTIVE_KEY);
  }
}

function readCooldownMs(): number {
  if (typeof window === 'undefined') return AUTO_RECOVERY_COOLDOWN_DEFAULT_MS;
  try {
    const raw = readSessionStorageItem(AUTO_RECOVERY_COOLDOWN_KEY);
    if (!raw) return AUTO_RECOVERY_COOLDOWN_DEFAULT_MS;
    return clampAutoRecoveryCooldownMs(Number(raw));
  } catch {
    return AUTO_RECOVERY_COOLDOWN_DEFAULT_MS;
  }
}

function persistCooldownMs(ms: number): void {
  if (typeof window === 'undefined') return;
  const clamped = clampAutoRecoveryCooldownMs(ms);
  if (clamped === AUTO_RECOVERY_COOLDOWN_DEFAULT_MS) {
    removeSessionStorageItem(AUTO_RECOVERY_COOLDOWN_KEY);
  } else {
    writeSessionStorageItem(AUTO_RECOVERY_COOLDOWN_KEY, String(clamped));
  }
}

function attemptSilentReload(reason: string, opportunityId: string | undefined): boolean {
  if (!isAutoRecoveryEffective()) {
    captureClientPosthogEvent(POSTHOG_EVENT_CHUNK_NOT_OPTED_IN, { opportunityId, reason });
    return false;
  }

  const previous = readRecoveryRecord();
  const now = Date.now();
  const cooldownMs = readCooldownMs();
  const attemptsSoFar = previous?.attempts ?? 0;

  if (attemptsSoFar >= AUTO_RECOVERY_MAX_ATTEMPTS) {
    captureClientPosthogEvent(POSTHOG_EVENT_CHUNK_BUDGET_EXHAUSTED, {
      opportunityId,
      reason,
      attempts: attemptsSoFar,
      maxAttempts: AUTO_RECOVERY_MAX_ATTEMPTS,
      previousReason: previous?.reason,
    });
    return false;
  }

  if (previous && now - previous.lastReloadAt < cooldownMs) {
    captureClientPosthogEvent(POSTHOG_EVENT_CHUNK_GAVE_UP, {
      opportunityId,
      reason,
      msSinceLastReload: now - previous.lastReloadAt,
      cooldownMs,
      attempts: attemptsSoFar,
      previousReason: previous.reason,
    });
    return false;
  }

  const nextAttempts = attemptsSoFar + 1;
  // Best-effort write — losing the counter is preferable to a permanent error card.
  writeRecoveryRecord(reason, nextAttempts);
  captureClientPosthogEvent(POSTHOG_EVENT_CHUNK_RECOVERED, {
    opportunityId,
    reason,
    attempts: nextAttempts,
    maxAttempts: AUTO_RECOVERY_MAX_ATTEMPTS,
  });

  window.location.reload();
  return true;
}

interface UseClientRecoveryOptions {
  opportunityId?: string;
  enabled?: boolean;
}

function isSessionLive(snapshotSession: { status?: string } | null | undefined): boolean {
  if (!snapshotSession) return false;
  const status = snapshotSession.status;
  return status !== 'ended' && status !== 'expired';
}

export function useClientRecovery(options: UseClientRecoveryOptions = {}): void {
  const { opportunityId, enabled = true } = options;
  const queryClient = useQueryClient();
  const installedRef = useRef(false);
  const bus = useSyncBusContext();

  // Mirror effective opt-in to sessionStorage (boundary reads synchronously).
  const optedInRef = useRef(false);
  const sessionLiveRef = useRef(false);

  const recomputeEffective = (source: 'snapshot' | 'flags_ping' | 'rep_event'): void => {
    const effective = optedInRef.current && sessionLiveRef.current;
    const previous = isAutoRecoveryEffective();
    if (effective === previous) return;
    persistAutoRecoveryEffective(effective);
    captureClientPosthogEvent(POSTHOG_EVENT_EFFECTIVE_CHANGED, {
      opportunityId,
      effective,
      optedIn: optedInRef.current,
      sessionLive: sessionLiveRef.current,
      source,
    });
  };

  const applyCooldown = (
    value: number | undefined,
    source: 'snapshot' | 'flags_ping' | 'rep_event',
  ): void => {
    if (typeof value !== 'number') return;
    const next = clampAutoRecoveryCooldownMs(value);
    const previous = readCooldownMs();
    if (next === previous) return;
    persistCooldownMs(next);
    captureClientPosthogEvent(POSTHOG_EVENT_COOLDOWN_CHANGED, {
      opportunityId,
      cooldownMs: next,
      previousCooldownMs: previous,
      source,
    });
  };

  // Don't overwrite the mirror before the bus has hydrated a snapshot.
  useEffect(() => {
    if (!enabled) return;
    if (!bus.lastSnapshot) return;
    optedInRef.current = bus.lastSnapshot.flags?.autoRecoveryEnabled === true;
    sessionLiveRef.current = isSessionLive(bus.lastSnapshot.session);
    recomputeEffective('snapshot');
    applyCooldown(bus.lastSnapshot.flags?.autoRecoveryCooldownMs, 'snapshot');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, opportunityId, bus.lastSnapshot]);

  useOnSyncEvent('flags', (payload) => {
    if (!enabled) return;
    optedInRef.current = payload?.autoRecoveryEnabled === true;
    recomputeEffective('flags_ping');
    applyCooldown(payload?.autoRecoveryCooldownMs, 'flags_ping');
  });

  useOnSyncEvent('client:auto-recovery-config', (payload) => {
    if (!enabled) return;
    if (typeof payload?.enabled === 'boolean') {
      optedInRef.current = payload.enabled === true;
      recomputeEffective('rep_event');
    }
    applyCooldown(payload?.cooldownMs, 'rep_event');
  });

  useOnSyncEvent('session:status', (payload) => {
    if (!enabled) return;
    sessionLiveRef.current = isSessionLive(payload as { status?: string });
    recomputeEffective('rep_event');
  });

  useOnSyncEvent('session:ended', () => {
    if (!enabled) return;
    sessionLiveRef.current = false;
    recomputeEffective('rep_event');
  });

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    if (installedRef.current) return;
    installedRef.current = true;

    const onError = (e: ErrorEvent): void => {
      if (!isChunkLoadError(e.error) && !isChunkLoadError({ message: e.message })) return;
      attemptSilentReload('chunk_load_error', opportunityId);
    };

    const onRejection = (e: PromiseRejectionEvent): void => {
      if (!isChunkLoadError(e.reason)) return;
      attemptSilentReload('chunk_load_rejection', opportunityId);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);

    return () => {
      installedRef.current = false;
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [enabled, opportunityId]);

  useOnSyncEvent('client:soft-refresh', (payload) => {
    if (!enabled) return;

    captureClientPosthogEvent(POSTHOG_EVENT_REMOTE_REFRESH, {
      opportunityId,
      mode: payload?.mode,
      reason: payload?.reason,
    });

    if (payload?.mode === 'page') {
      writeRemoteRefreshRecord(`remote_${payload.reason ?? 'rep_initiated'}`);
      window.location.reload();
      return;
    }

    writeRemoteRefreshRecord(`remote_queries_${payload.reason ?? 'rep_initiated'}`);
    queryClient.invalidateQueries();
  });
}

export const __testing = {
  isChunkLoadError,
  isAutoRecoveryEffective,
  persistAutoRecoveryEffective,
  readCooldownMs,
  persistCooldownMs,
  readRecoveryRecord,
  writeRecoveryRecord,
  writeRemoteRefreshRecord,
  isSessionLive,
  RECOVERY_STORAGE_KEY,
  AUTO_RECOVERY_EFFECTIVE_KEY,
  AUTO_RECOVERY_COOLDOWN_KEY,
  RECOVERY_COOLDOWN_MS: AUTO_RECOVERY_COOLDOWN_DEFAULT_MS,
  MAX_ATTEMPTS: AUTO_RECOVERY_MAX_ATTEMPTS,
};
